import { ExternalLink, FileText, Award } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { FadeUp } from "../components/Reveal";
import { VideoPlayer } from "../components/VideoPlayer";
import { PUBLICATIONS, CASE_REPORTS, IMAGES } from "../data/content";
import { useLang } from "../i18n/i18n";

export default function Research() {
  const { t } = useLang();
  return (
    <div data-testid="research-page">
      <PageHero
        index="/ 03"
        kicker={t.research.kicker}
        titleLines={[t.research.pageTitle1, t.research.pageTitle2]}
        subtitle={t.research.pageSub}
      />

      <section data-testid="publications-section" className="bg-ink px-6 py-24 md:px-12 md:py-32">
        <div className="mx-auto max-w-[1500px]">
          <FadeUp>
            <div className="mb-14 flex items-center gap-4">
              <FileText className="h-5 w-5 text-crimson" />
              <span className="font-mono text-xs uppercase tracking-[0.28em] text-white/60">{t.research.pubHeading}</span>
            </div>
          </FadeUp>

          <div className="border-t border-white/10">
            {PUBLICATIONS.map((p, i) => (
              <FadeUp key={i} delay={i * 0.05}>
                <a
                  href={`https://doi.org/${p.doi}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`publication-${i}`}
                  className="group grid gap-4 border-b border-white/10 py-8 transition-colors duration-300 hover:bg-white/[0.02] md:grid-cols-[auto_1fr_auto] md:items-center md:gap-10"
                >
                  <span className="font-serif text-3xl font-light text-white/25 transition-colors group-hover:text-crimson md:text-4xl">0{i + 1}</span>
                  <div>
                    <h3 className="text-lg leading-snug text-white transition-colors group-hover:text-crimson md:text-2xl">{p.title}</h3>
                    <p className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 font-mono text-xs text-white/45">
                      <span className="uppercase tracking-[0.15em] text-white/65">{p.journal} {p.year}</span>
                      <span className="text-crimson">DOI: {p.doi}</span>
                    </p>
                  </div>
                  <ExternalLink className="hidden h-5 w-5 text-white/30 transition-all group-hover:text-crimson md:block" />
                </a>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="case-reports-section" className="relative overflow-hidden bg-ink2 px-6 py-24 md:px-12 md:py-32">
        <div
          className="pointer-events-none absolute inset-0 opacity-10"
          style={{ backgroundImage: `url(${IMAGES.ecg})`, backgroundSize: "cover", backgroundPosition: "center" }}
        />
        <div className="relative mx-auto max-w-[1500px]">
          <FadeUp>
            <div className="mb-14 flex items-center gap-4">
              <Award className="h-5 w-5 text-crimson" />
              <span className="font-mono text-xs uppercase tracking-[0.28em] text-white/60">{t.research.caseHeading}</span>
            </div>
          </FadeUp>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {CASE_REPORTS.map((c, i) => (
              <FadeUp key={i} delay={i * 0.07}>
                <div className="group h-full border border-white/10 bg-ink p-8 transition-colors duration-400 hover:border-crimson/60">
                  <span className="font-mono text-xs text-crimson">{t.research.caseLabel} 0{i + 1}</span>
                  <h3 className="mt-5 font-serif text-2xl font-light leading-snug text-white">{c.title}</h3>
                  <p className="mt-4 text-sm leading-relaxed text-white/55">{c.body}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-ink px-6 py-24 md:px-12 md:py-32">
        <div className="mx-auto max-w-[1300px]">
          <FadeUp>
            <span className="font-mono text-xs uppercase tracking-[0.28em] text-crimson">{t.research.videoKicker}</span>
            <h2 className="mt-5 max-w-2xl font-serif text-4xl font-light leading-tight text-white md:text-5xl">{t.research.videoTitle}</h2>
            <p className="mt-6 max-w-2xl leading-relaxed text-white/55">{t.research.videoSub}</p>
          </FadeUp>
          <FadeUp delay={0.1} className="mt-12">
            <VideoPlayer poster={IMAGES.ecg} />
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
